// /admin/integrations/new?kind=… and /admin/integrations/:id — one integration.
//
// THE FORM IS DRAWN FROM THE REGISTRY, never from a literal in this file. `kinds` off
// `GET /api/admin/integrations` carries each kind's config and secret field declarations
// (src/integrations/registry.js), and the three field types it defines — `string`, `json`,
// `enum` — are the three this screen renders. A kind added to the registry gets a working
// form here with no change to this component, which is the entire point of the feature.
//
// CONFIG AND SECRETS ARE NOT THE SAME KIND OF FIELD, and the difference is the reason
// this screen is not the descriptor form:
//
//   config   read back, edited in place, diffed against what is stored.
//   secrets  WRITE ONLY. The API masks them to `{ set: true|false|null }` and no endpoint
//            returns the value, so the box is always empty when the screen opens and
//            "empty" therefore means "unchanged", never "erase". Clearing one is an
//            explicit control that sends `null`; see integrations-form.js.
//
// PRESENTATION, NEVER AUTHORIZATION. The routes are admin-only and refuse on their own.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { apiFetch, ApiError } from '../../api/client.js';
import { useAtLeastRole } from '../../session/SessionProvider.jsx';
import { renderInput } from '../../components/create/CustomFields.jsx';
import { formatDateTime } from '../../components/tickets/format.js';
import { consoleErrorMessage, refusalBanner } from './descriptor-form.js';
import {
  initialIntegrationValues, changedIntegrationValues, healthLine, triggerBodies,
  parseJsonObjectDraft, secretsKeyBanner, secretStateLabel,
} from './integrations-form.js';
import { supportPath } from '../../support-path.js';

/**
 * Copy, with the failure handled.
 *
 * The clipboard is FEATURE-DETECTED AT CLICK TIME AND THE PROMISE IS CAUGHT, exactly as
 * KeywordPanel does it: what varies between environments is not whether
 * `navigator.clipboard` exists but whether the write is permitted, and a `.then()` that
 * reports "Copied" without a `.catch()` is a button that claims success and did nothing.
 * On any failure the text is SELECTED instead, so Ctrl+C finishes the job.
 */
function CopyButton({ text, targetRef }) {
  const [state, setState] = useState('idle');
  const timer = useRef(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function announce(next) {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 1500);
  }

  function selectText() {
    const node = targetRef?.current;
    if (!node || typeof window === 'undefined') return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  async function copy() {
    if (!navigator.clipboard?.writeText) {
      selectText();
      announce('manual');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      announce('copied');
    } catch {
      selectText();
      announce('manual');
    }
  }

  return (
    <span className="flex shrink-0 items-center gap-2">
      {state === 'manual' && (
        <span className="text-xs text-ink-secondary">Selected — press Ctrl+C.</span>
      )}
      <button
        type="button"
        className="btn-ghost shrink-0 px-2 py-0.5 text-xs ring-1 ring-line-strong"
        onClick={() => { void copy(); }}
      >
        {state === 'copied' ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

/** One line of text to be pasted somewhere else, with its Copy button. */
function CopyRow({ label, value }) {
  const ref = useRef(null);
  return (
    <div className="mt-2">
      <p className="text-xs font-medium text-ink-secondary">{label}</p>
      <div className="mt-1 flex items-start justify-between gap-2 rounded-md bg-sunken p-2">
        <code ref={ref} className="min-w-0 font-mono text-xs break-all text-ink">{value}</code>
        <CopyButton text={value} targetRef={ref} />
      </div>
    </div>
  );
}

/**
 * A `type: 'json'` field.
 *
 * THE DRAFT IS LOCAL STATE, for the reason DescriptorForm's ActionsField states: a
 * controlled textarea whose `value` is `JSON.stringify(parsedValue)` cannot be typed
 * into — the first keystroke that makes the text incomplete fails to parse, the parsed
 * value does not change, and React re-renders the old text over what was just typed.
 *
 * Unreadable text BLOCKS the save rather than resolving to `{}`. A group→queue map that
 * round-trips to empty because a textarea could not parse it is data loss with a success
 * message on top of it, and its consequence here is every Zendesk group silently landing
 * in the default queue.
 */
function JsonField({ id, help, value, fieldKey, setValue, onDraftError }) {
  const [draft, setDraft] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [parseError, setParseError] = useState(null);

  useEffect(() => {
    onDraftError(fieldKey, parseError);
    // Leaving a blocked field behind must not disable Save forever.
    return () => onDraftError(fieldKey, null);
  }, [parseError, fieldKey]);

  return (
    <>
      <textarea
        id={id}
        rows={6}
        spellCheck={false}
        className="field-input font-mono text-xs"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          const result = parseJsonObjectDraft(event.target.value);
          setParseError(result.ok ? null : result.error);
          if (result.ok) setValue(fieldKey, result.value);
        }}
      />
      {/* The help sits BETWEEN the control and its error, so the order down the column is
          the same on every field on this page: control, what it is for, what is wrong
          with it. The parse error is rendered here rather than by the caller because the
          draft it describes is local to this component. */}
      {help && <p className="mt-1 text-xs text-ink-muted">{help}</p>}
      {parseError && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{parseError}</p>}
    </>
  );
}

/**
 * A write-only credential.
 *
 * THE BOX IS ALWAYS EMPTY, and the sentence beside it is what stops that reading as "there
 * is nothing stored". Three states, not two — `secretStateLabel` says why the third exists.
 *
 * Clearing is a SEPARATE, explicit control. It has to be: an empty box means "unchanged",
 * so there is no keystroke that could mean "erase", and without this button a credential
 * could be replaced but never removed.
 */
function SecretField({ field, mask, value, setSecret, disabled }) {
  const id = `integration-secret-${field.key}`;
  const cleared = value === null;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label className="field-label mb-0" htmlFor={id}>{field.label ?? field.key}</label>
        <span className="text-xs text-ink-muted">{secretStateLabel(mask)}</span>
      </div>
      <input
        id={id}
        type="password"
        autoComplete="new-password"
        className="field-input mt-1"
        placeholder={cleared ? 'Will be cleared when you save' : 'Leave blank to keep the current value'}
        disabled={disabled || cleared}
        value={cleared ? '' : (value ?? '')}
        onChange={(event) => setSecret(field.key, event.target.value)}
      />
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs text-ink-muted">
          {field.help ?? 'Stored encrypted. It is never shown again.'}
        </p>
        {!disabled && (
          cleared ? (
            <button
              type="button"
              className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
              onClick={() => setSecret(field.key, '')}
            >
              Keep it after all
            </button>
          ) : (
            <button
              type="button"
              className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
              disabled={mask?.set === false}
              onClick={() => setSecret(field.key, null)}
            >
              Clear
            </button>
          )
        )}
      </div>
      {cleared && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
          This credential will be erased when you save.
        </p>
      )}
    </div>
  );
}

/**
 * The half of the setup that is NOT in this platform (spec §6).
 *
 * READ-ONLY, and deliberately so: nothing on this panel is stored here, because none of it
 * is ours to store. The webhook and the two triggers live in Zendesk Admin Center, and
 * this panel exists because the URL depends on where this app is deployed and the trigger
 * bodies are EXACT — `readZendeskId` (src/routes/zendesk.js) reads `zendesk_id` and
 * nothing else, with no `ticket_id` alias, so a body retyped from memory is a trigger that
 * fires forever and delivers nothing. Copy buttons, not instructions to retype.
 */
function ZendeskSetup() {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const setup = triggerBodies(origin);

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Set up in Zendesk</h2>
      <p className="mt-1 text-xs text-ink-secondary">
        These three things are configured in Zendesk Admin Center, not here. Apps and
        integrations → Webhooks → Create: the endpoint below, method POST, JSON, no
        authentication (the signature is the authentication). Reveal the signing secret it
        shows you and paste it into "Webhook signing secret" above.
      </p>

      <CopyRow label="Webhook endpoint" value={setup.url} />

      {setup.triggers.map((trigger) => (
        <div key={trigger.event} className="mt-3 border-t border-line pt-3">
          <p className="text-xs font-medium text-ink">
            Trigger "{trigger.title}" — condition: {trigger.when}, action: Notify active
            webhook, with this body:
          </p>
          <CopyRow label="JSON body" value={trigger.body} />
        </div>
      ))}

      <p className="mt-3 text-xs text-ink-muted">
        Put both triggers at the BOTTOM of the trigger order so they never pre-empt the
        desk's own. A comment this bridge posts to Zendesk fires the "updated" trigger,
        which enqueues a pull that finds nothing new — one cheap round trip, no loop.
      </p>
    </section>
  );
}

export default function IntegrationEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new';
  // Read as a STRING rather than depending on the `searchParams` object: that object is a
  // new instance on every location change, so a `useCallback` keyed on it would re-run the
  // load on renders that changed nothing — and keyed on nothing at all, `/new?kind=a` →
  // `/new?kind=b` would keep drawing the first kind's form.
  const kindParam = searchParams.get('kind');
  const navigate = useNavigate();
  const mayWrite = useAtLeastRole('support', 'admin');

  const [meta, setMeta] = useState(null);
  const [kind, setKind] = useState(null);
  const [row, setRow] = useState(null);
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  // Fields whose editor cannot currently read what is typed in them. Saving while one is
  // set would send the last good value under a new one's name.
  const [draftErrors, setDraftErrors] = useState({});

  const load = useCallback(async (signal) => {
    // The LIST endpoint is what carries `kinds` and the encryption-key state; there is no
    // separate registry endpoint, and one request that already exists beats inventing a
    // second. Both calls go out together rather than in sequence.
    const calls = [apiFetch('/admin/integrations', { signal })];
    if (!isNew) calls.push(apiFetch(`/admin/integrations/${encodeURIComponent(id)}`, { signal }));
    const [index, one] = await Promise.all(calls);

    const wanted = isNew ? kindParam : one.data.kind;
    const found = (index.kinds ?? []).find((entry) => entry.kind === wanted) ?? null;
    setMeta(index);
    setKind(found);
    setRow(one?.data ?? null);
    setValues(initialIntegrationValues(found, one?.data ?? null));
    if (isNew && !found) {
      setError(wanted
        ? `This build has no integration kind named "${wanted}".`
        : 'Pick a kind to add from the Integrations page.');
    }
  }, [id, isNew, kindParam]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal)
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [load]);

  const setConfig = (key, value) => setValues((prev) => ({
    ...prev, config: { ...prev.config, [key]: value },
  }));
  const setSecret = (key, value) => setValues((prev) => ({
    ...prev, secrets: { ...prev.secrets, [key]: value },
  }));

  const noteDraftError = (key, message) => setDraftErrors((prev) => {
    if (!message) {
      const { [key]: _gone, ...rest } = prev;
      return rest;
    }
    return { ...prev, [key]: message };
  });

  function reportFailure(err) {
    if (err instanceof ApiError && Array.isArray(err.details?.fields)) {
      // The server names a config problem by its BARE key (`subdomain`) and a credential
      // problem as `secrets.api_token`, because that is the shape the request had. Both
      // are mapped here rather than in descriptor-form.js: `mapFieldErrors` is built for
      // a flat descriptor and would file every one of these under "no control".
      const mapped = {};
      const unattached = [];
      for (const problem of err.details.fields) {
        const raw = String(problem?.field ?? '');
        const key = raw.replace(/^(config|secrets)\./, '');
        const message = String(problem?.message ?? '').trim() || 'Not valid.';
        const known = (kind?.config ?? []).some((f) => f.key === key)
          || (kind?.secrets ?? []).some((f) => f.key === key)
          || key === 'name';
        if (known) mapped[key] = mapped[key] ? `${mapped[key]} ${message}` : message;
        else unattached.push(raw.startsWith('(') || raw === '' ? message : `${raw}: ${message}`);
      }
      setFieldErrors(mapped);
      const { headline, list } = refusalBanner(mapped, unattached);
      setFormErrors(list);
      setError(headline ?? consoleErrorMessage(err));
      return;
    }
    setError(consoleErrorMessage(err));
  }

  async function save() {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setFormErrors([]);
    try {
      const body = changedIntegrationValues(kind, values, row);
      if (!isNew && Object.keys(body).length === 0) {
        navigate(supportPath('/admin/integrations'));
        return;
      }
      const saved = await apiFetch(
        isNew ? '/admin/integrations' : `/admin/integrations/${encodeURIComponent(id)}`,
        { method: isNew ? 'POST' : 'PATCH', body },
      );
      if (isNew) {
        // Straight on to the row's own screen rather than back to the list: a new
        // integration is created DISABLED and with no credentials, so the next thing to
        // do is always here.
        navigate(supportPath(`/admin/integrations/${encodeURIComponent(saved.data.id)}`), { replace: true });
        return;
      }
      // Re-seed from the saved row rather than navigating away: `secrets` masks move, and
      // the admin's next act is usually Test connection.
      setRow(saved.data);
      setValues(initialIntegrationValues(kind, saved.data));
      setTestResult(null);
    } catch (err) {
      reportFailure(err);
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      // 200 with `ok: false` is the normal answer for a refused credential — it is
      // Zendesk's opinion of the token, not a failure of this request — so the result is
      // read off the body rather than off a status code.
      const body = await apiFetch(`/admin/integrations/${encodeURIComponent(id)}/test`, { method: 'POST' });
      setTestResult(body.data);
      // The server also writes the result to `status_json`, so re-read the row and the
      // health line agrees with the sentence just printed.
      const fresh = await apiFetch(`/admin/integrations/${encodeURIComponent(id)}`);
      setRow(fresh.data);
    } catch (err) {
      reportFailure(err);
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    setError(null);
    setFormErrors([]);
    try {
      await apiFetch(`/admin/integrations/${encodeURIComponent(id)}`, { method: 'DELETE' });
      navigate(supportPath('/admin/integrations'));
    } catch (err) {
      reportFailure(err);
    }
  }

  if (error && !values) {
    return (
      <div className="max-w-3xl">
        <Link to={supportPath('/admin/integrations')} className="text-sm text-ink-secondary hover:underline">
          &larr; Integrations &amp; API
        </Link>
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          {error}
        </div>
      </div>
    );
  }
  if (!values) return <p className="text-sm text-ink-muted">Loading…</p>;

  const health = healthLine(row?.status, formatDateTime);
  const blockingDrafts = Object.keys(draftErrors);
  const lastTest = row?.status?.last_test_at ?? null;
  const keyBanner = secretsKeyBanner(meta);

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to={supportPath('/admin/integrations')} className="text-sm text-ink-secondary hover:underline">
          &larr; Integrations &amp; API
        </Link>
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink">
          {isNew ? `Add ${kind?.label ?? 'integration'}` : (row?.name ?? 'Integration')}
        </h1>
        {kind?.description && <p className="text-sm text-ink-secondary">{kind.description}</p>}
      </div>

      {!mayWrite && (
        <p className="text-sm text-ink-secondary">
          Integrations are administered by support administrators. This is read-only for you.
        </p>
      )}

      {/* The same banner the list shows, repeated here because this is the screen where
          it bites: a credential save is the one thing a missing key refuses. */}
      {keyBanner && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {keyBanner.headline}
          </p>
          <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">{keyBanner.detail}</p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          {formErrors.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-sm text-red-700 dark:text-red-300">
              {formErrors.map((message) => <li key={message}>{message}</li>)}
            </ul>
          )}
        </div>
      )}

      <section className="rounded-lg border border-line bg-surface p-4">
        <fieldset className="grid gap-4" disabled={!mayWrite}>
          <div>
            <label className="field-label" htmlFor="integration-name">
              Name<span className="text-red-600 dark:text-red-400"> *</span>
            </label>
            <input
              id="integration-name"
              className="field-input"
              value={values.name}
              onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
            />
            <p className="mt-1 text-xs text-ink-muted">
              What this connection is called in this console. Only a label.
            </p>
            {fieldErrors.name && (
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">{fieldErrors.name}</p>
            )}
          </div>

          {(kind?.config ?? []).map((field) => {
            const fieldId = `integration-config-${field.key}`;
            return (
              <div key={field.key}>
                <label className="field-label" htmlFor={fieldId}>
                  {field.label ?? field.key}
                  {field.required && <span className="text-red-600 dark:text-red-400"> *</span>}
                </label>
                {field.type === 'json' ? (
                  <JsonField
                    id={fieldId}
                    help={field.help}
                    value={values.config[field.key]}
                    fieldKey={field.key}
                    setValue={setConfig}
                    onDraftError={noteDraftError}
                  />
                ) : (
                  // `renderInput` is the console's one field renderer, shared with the
                  // ticket create screen — a fix to its focus rings or dark-mode contrast
                  // lands on every form at once, which a near-copy here would not get.
                  renderInput(
                    {
                      key: field.key,
                      data_type: field.type === 'enum' ? 'select' : 'text',
                      options: (field.values ?? []).map((v) => ({ value: v, label: String(v) })),
                    },
                    fieldId,
                    values.config[field.key],
                    setConfig,
                  )
                )}
                {/* A json field prints its own help, between the textarea and the parse
                    error that belongs to its local draft. */}
                {field.type !== 'json' && field.help && (
                  <p className="mt-1 text-xs text-ink-muted">{field.help}</p>
                )}
                {fieldErrors[field.key] && (
                  <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                    {fieldErrors[field.key]}
                  </p>
                )}
              </div>
            );
          })}
        </fieldset>
      </section>

      {(kind?.secrets ?? []).length > 0 && (
        <section className="rounded-lg border border-line bg-surface p-4">
          <h2 className="text-sm font-semibold text-ink">Credentials</h2>
          <p className="mt-1 text-xs text-ink-secondary">
            These are stored encrypted and are never shown again. Leaving a box blank keeps
            the value already stored — it does not erase it.
          </p>
          <fieldset className="mt-3 grid gap-4" disabled={!mayWrite}>
            {kind.secrets.map((field) => (
              <div key={field.key}>
                <SecretField
                  field={field}
                  mask={row?.secrets?.[field.key] ?? (isNew ? { set: false } : undefined)}
                  value={values.secrets[field.key]}
                  setSecret={setSecret}
                  disabled={!mayWrite}
                />
                {fieldErrors[field.key] && (
                  <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                    {fieldErrors[field.key]}
                  </p>
                )}
              </div>
            ))}
          </fieldset>
        </section>
      )}

      {!isNew && (
        <section className="rounded-lg border border-line bg-surface p-4">
          <label className="flex items-center gap-2 text-sm text-ink" htmlFor="integration-enabled">
            <input
              id="integration-enabled"
              type="checkbox"
              className="size-4 rounded accent-brand-600
                         focus-visible:outline-2 focus-visible:outline-offset-2
                         focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
              disabled={!mayWrite}
              checked={values.is_enabled}
              onChange={(event) => setValues((prev) => ({ ...prev, is_enabled: event.target.checked }))}
            />
            Enabled
          </label>
          <p className="mt-1 text-xs text-ink-muted">
            While this is on, the scheduler runs this integration's tasks and re-reads the
            row every run, so turning it on or off takes effect within one interval and
            needs no restart.
          </p>
          {fieldErrors.is_enabled && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-300">{fieldErrors.is_enabled}</p>
          )}

          <div className="mt-3 border-t border-line pt-3">
            <p className="text-xs text-ink-secondary">
              {health.text ?? 'No activity reported yet.'}
            </p>
            {health.error && (
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                Last error: {health.error}
              </p>
            )}
            {lastTest && (
              <p className="mt-1 text-xs text-ink-muted">
                Last tested {formatDateTime(lastTest)} —{' '}
                {row.status.last_test_ok ? 'succeeded' : 'failed'}
                {row.status.last_test_detail ? `: ${row.status.last_test_detail}` : ''}
              </p>
            )}
          </div>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {mayWrite && (
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            disabled={saving || blockingDrafts.length > 0 || (isNew && !kind)}
            onClick={() => { void save(); }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        <Link to={supportPath('/admin/integrations')} className="btn-ghost ring-1 ring-line-strong">
          {mayWrite ? 'Cancel' : 'Back'}
        </Link>

        {!isNew && (
          <button
            type="button"
            className="btn-ghost ring-1 ring-line-strong"
            disabled={testing}
            onClick={() => { void runTest(); }}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
        )}

        {/* REFUSED WHILE ENABLED, by `deleteIntegration` — deleting a running integration
            would stop a sync mid-flight and take its credentials with it. The button is
            disabled off the SAVED state, not the checkbox above it, because that is what
            the server will look at; and the reason is printed rather than left to a 422
            an admin has to provoke to read. */}
        {!isNew && mayWrite && (
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong disabled:opacity-50"
              disabled={row?.is_enabled === true}
              onClick={() => {
                if (window.confirm(
                  `Delete "${row?.name}" permanently? Its stored credentials go with it. `
                  + 'Tickets and messages it created stay, as history.',
                )) void remove();
              }}
            >
              Delete
            </button>
            {row?.is_enabled === true && (
              <span className="text-xs text-ink-muted">
                Disable it first, and save, so its sync stops cleanly and you can see that
                it has.
              </span>
            )}
          </span>
        )}
      </div>

      {blockingDrafts.length > 0 && (
        <p className="text-xs text-red-700 dark:text-red-300">
          Save is unavailable while {blockingDrafts.join(', ')} cannot be read. The value
          already saved has not been changed.
        </p>
      )}

      {testResult && (
        <div className={testResult.ok
          ? 'rounded-lg border border-green-300 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950'
          : 'rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950'}
        >
          <p className={testResult.ok
            ? 'text-sm text-green-800 dark:text-green-300'
            : 'text-sm text-red-700 dark:text-red-300'}
          >
            {testResult.ok ? 'Connected. ' : 'Not connected. '}
            {testResult.detail}
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            This tests what is SAVED, not what is typed above.
          </p>
        </div>
      )}

      {/* The Zendesk half of the setup, which is not in this platform at all. Keyed on the
          kind, so it appears for a Zendesk row and for nothing else. */}
      {kind?.kind === 'zendesk' && <ZendeskSetup />}
    </div>
  );
}
